import React, { useEffect, useMemo, useState } from 'react';

import styles from './Dashboard.module.css';
import styles_global from '../css/global.module.css';

import { useHttpClient } from '../hooks/useHttpClient';
import { useNotificacoes } from '../hooks/useNotificacoes';

function getAnoAtual() {
    return new Date().getFullYear();
}

function formatarNumero(valor, casas = 2) {
    if (valor === null || valor === undefined) return '0';
    const n = Number(valor);
    if (Number.isNaN(n)) return String(valor);
    return n.toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas });
}

function formatarKwh(valor) {
    return `${formatarNumero(valor, 2)} kWh`;
}

function formatarReal(valor) {
    return `R$ ${formatarNumero(valor, 2)}`;
}

function formatarPercent(valor) {
    if (valor === null || valor === undefined) return '0%';
    const n = Number(valor);
    if (Number.isNaN(n)) return `${valor}%`;
    return `${n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
}

function corVariacaoClass(stylesCss, v) {
    const n = Number(v);
    if (Number.isNaN(n)) return '';
    if (n > 0) return stylesCss.positivo;
    if (n < 0) return stylesCss.negativo;
    return stylesCss.neutro;
}

function montarQuery({ ano, periodo, empresaId, usinaId }) {
    return new URLSearchParams({
        ano: String(ano),
        periodo: String(periodo),
        ...(empresaId ? { empresaId: String(empresaId) } : {}),
        ...(usinaId ? { usinaId: String(usinaId) } : {}),
    }).toString();
}

/**
 * Gráfico de linha simples (SVG) sem libs externas
 * data: [{ competencia: 'YYYY-MM', valor: 123.45 }, ...]
 */
function LineChartMini({ title, data = [], formatter }) {
    const width = 640;
    const height = 220;
    const padL = 40;
    const padR = 14;
    const padT = 16;
    const padB = 34;

    const points = useMemo(() => {
        const serie = Array.isArray(data) ? data : [];
        const valores = serie.map(d => Number(d?.valor ?? 0)).filter(v => !Number.isNaN(v));

        const min = valores.length ? Math.min(...valores) : 0;
        const max = valores.length ? Math.max(...valores) : 0;

        // evita divisão por zero
        const span = max - min || 1;

        const xs = (i) => {
            const n = Math.max(serie.length - 1, 1);
            return padL + (i * (width - padL - padR)) / n;
        };
        const ys = (v) => {
            const t = (v - min) / span;
            return (height - padB) - t * (height - padT - padB);
        };

        const pts = serie.map((d, i) => {
            const v = Number(d?.valor ?? 0);
            return { x: xs(i), y: ys(Number.isNaN(v) ? 0 : v), v, comp: d?.competencia ?? '' };
        });

        return { pts, min, max };
    }, [data]);

    const pathD = useMemo(() => {
        if (!points.pts.length) return '';
        return points.pts
            .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`)
            .join(' ');
    }, [points]);

    const labels = useMemo(() => {
        const serie = Array.isArray(data) ? data : [];
        if (serie.length <= 1) return [];
        // mostra até 6 labels para não poluir
        const maxLabels = 6;
        const step = Math.max(1, Math.ceil(serie.length / maxLabels));
        const result = [];
        for (let i = 0; i < serie.length; i += step) {
            result.push({ i, comp: String(serie[i]?.competencia ?? '') });
        }
        // garante último
        if (result.length && result[result.length - 1].i !== serie.length - 1) {
            result.push({ i: serie.length - 1, comp: String(serie[serie.length - 1]?.competencia ?? '') });
        }
        return result;
    }, [data]);

    return (
        <div className={styles.graficoBox}>
            <div className={styles.graficoHeader}>
                <div className={styles.graficoTitulo}>{title}</div>
                <div className={styles.graficoRange}>
                    <span>Mín: <b>{formatter(points.min)}</b></span>
                    <span>Máx: <b>{formatter(points.max)}</b></span>
                </div>
            </div>

            {(!data || !data.length) ? (
                <div className={styles.semDadosGrafico}>Sem dados para o período.</div>
            ) : (
                <svg viewBox={`0 0 ${width} ${height}`} className={styles.svgChart} role="img" aria-label={title}>
                    {/* eixos */}
                    <line x1={padL} y1={padT} x2={padL} y2={height - padB} className={styles.axis} />
                    <line x1={padL} y1={height - padB} x2={width - padR} y2={height - padB} className={styles.axis} />

                    {/* grade horizontal leve (3 linhas) */}
                    {[0.25, 0.5, 0.75].map((t, idx) => {
                        const y = (height - padB) - t * (height - padT - padB);
                        return <line key={idx} x1={padL} y1={y} x2={width - padR} y2={y} className={styles.grid} />;
                    })}

                    {/* linha */}
                    <path d={pathD} className={styles.line} />

                    {/* pontos */}
                    {points.pts.map((p, idx) => (
                        <circle key={idx} cx={p.x} cy={p.y} r="3" className={styles.dot}>
                            <title>{p.comp}: {formatter(p.v)}</title>
                        </circle>
                    ))}

                    {/* labels X */}
                    {labels.map((l) => {
                        const x = padL + (l.i * (width - padL - padR)) / Math.max((data.length - 1), 1);
                        const txt = l.comp;
                        return (
                            <text key={l.i} x={x} y={height - 12} textAnchor="middle" className={styles.xLabel}>
                                {txt}
                            </text>
                        );
                    })}
                </svg>
            )}
        </div>
    );
}

export default function Dashboard({ empresaId, usinaId }) {
    const { get } = useHttpClient();
    const { exibirErro } = useNotificacoes();

    const [ano, setAno] = useState(getAnoAtual());
    const [periodo, setPeriodo] = useState('ULTIMOS_6');

    const [loading, setLoading] = useState(false);
    const [dados, setDados] = useState(null);
    const [comparativo, setComparativo] = useState(null);

    const visao = useMemo(() => (usinaId ? 'USINA' : 'EMPRESA'), [usinaId]);

    const anosDisponiveis = useMemo(() => {
        const atual = getAnoAtual();
        return [atual - 2, atual - 1, atual, atual + 1];
    }, []);

    const consultar = async () => {
        try {
            setLoading(true);

            const query = montarQuery({ ano, periodo, empresaId, usinaId });

            const resp = await get(`/hello-sol/dashboard/resumo?${query}`);
            setDados(resp);

            // comparativo: mês referência x anterior
            try {
                const comp = await get(`/hello-sol/dashboard/comparativo?${query}`);
                setComparativo(comp);
            } catch (e) {
                // comparativo não deve quebrar o dashboard
                setComparativo(null);
            }
        } catch (e) {
            console.error(e);
            exibirErro('Erro ao carregar dashboard.');
            setDados(null);
            setComparativo(null);
        } finally {
            setLoading(false);
        }
    };

    // primeira carga (padrão do projeto: carrega ao entrar)
    useEffect(() => {
        consultar();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const titulo = visao === 'USINA' ? 'Dashboard (Usina)' : 'Dashboard (Empresa)';

    const resumo = dados?.resumo || {};
    const periodoDescricao = dados?.periodoDescricao || '';

    const serieProducao = Array.isArray(dados?.serieProducao) ? dados.serieProducao : [];
    const serieConsumo = Array.isArray(dados?.serieConsumo) ? dados.serieConsumo : [];
    const serieEconomia = Array.isArray(dados?.serieEconomia) ? dados.serieEconomia : [];

    const statusPorCompetencia = Array.isArray(resumo?.statusPorCompetencia) ? resumo.statusPorCompetencia : [];

    return (
        <div className={styles_global.container}>
            <div className={styles.header}>
                <h2>{titulo}</h2>

                <div className={styles.filtros}>
                    <div className={styles.campoFiltro}>
                        <label>Período</label>
                        <select value={periodo} onChange={(e) => setPeriodo(e.target.value)}>
                            <option value="ULTIMOS_3">Últimos 3 meses</option>
                            <option value="ULTIMOS_6">Últimos 6 meses</option>
                            <option value="ULTIMOS_12">Últimos 12 meses</option>
                            <option value="ANO_COMPLETO">Ano completo</option>
                        </select>
                    </div>

                    <div className={styles.campoFiltro}>
                        <label>Ano</label>
                        <select value={ano} onChange={(e) => setAno(Number(e.target.value))}>
                            {anosDisponiveis.map((a) => (
                                <option key={a} value={a}>{a}</option>
                            ))}
                        </select>
                    </div>

                    <button className={styles.botaoConsultar} type="button" onClick={consultar} disabled={loading}>
                        {loading ? 'Consultando...' : 'Consultar'}
                    </button>
                </div>
            </div>

            {periodoDescricao ? (
                <div className={styles.periodo}>{periodoDescricao}</div>
            ) : null}

            {/* Cards do MÊS REFERÊNCIA (o que o usuário mais quer ver) */}
            <div className={styles.cards}>
                <div className={styles.card}>
                    <div className={styles.cardTitulo}>MÊS REFERÊNCIA</div>
                    <div className={styles.cardValor}>{resumo?.competenciaReferencia || '—'}</div>
                    <div className={styles.cardSub}>Último mês do período selecionado</div>
                </div>

                <div className={styles.card}>
                    <div className={styles.cardTitulo}>PRODUÇÃO (MÊS)</div>
                    <div className={styles.cardValor}>{formatarKwh(resumo?.producaoMes)}</div>
                </div>

                <div className={styles.card}>
                    <div className={styles.cardTitulo}>CONSUMO (MÊS)</div>
                    <div className={styles.cardValor}>{formatarKwh(resumo?.consumoMes)}</div>
                </div>

                <div className={styles.card}>
                    <div className={styles.cardTitulo}>ECONOMIA (MÊS)</div>
                    <div className={styles.cardValor}>{formatarReal(resumo?.economiaMes)}</div>
                </div>
            </div>

            {/* Comparativo mês referência x anterior */}
            {comparativo && (
                <div className={styles.comparativo}>
                    <div className={styles.comparativoTitulo}>
                        Comparativo: <b>{comparativo.competenciaAtual}</b> × <b>{comparativo.competenciaAnterior}</b>
                    </div>

                    <div className={styles.comparativoGrid}>
                        <div className={styles.compCard}>
                            <div className={styles.compTitulo}>Produção</div>
                            <div className={styles.compLinha}><span>Atual:</span><b>{formatarKwh(comparativo.producaoAtual)}</b></div>
                            <div className={styles.compLinha}><span>Anterior:</span><b>{formatarKwh(comparativo.producaoAnterior)}</b></div>
                            <div className={`${styles.compVar} ${corVariacaoClass(styles, comparativo.producaoVariacao)}`}>
                                {formatarPercent(comparativo.producaoVariacao)}
                            </div>
                        </div>

                        <div className={styles.compCard}>
                            <div className={styles.compTitulo}>Consumo</div>
                            <div className={styles.compLinha}><span>Atual:</span><b>{formatarKwh(comparativo.consumoAtual)}</b></div>
                            <div className={styles.compLinha}><span>Anterior:</span><b>{formatarKwh(comparativo.consumoAnterior)}</b></div>
                            <div className={`${styles.compVar} ${corVariacaoClass(styles, comparativo.consumoVariacao)}`}>
                                {formatarPercent(comparativo.consumoVariacao)}
                            </div>
                        </div>

                        <div className={styles.compCard}>
                            <div className={styles.compTitulo}>Economia</div>
                            <div className={styles.compLinha}><span>Atual:</span><b>{formatarReal(comparativo.economiaAtual)}</b></div>
                            <div className={styles.compLinha}><span>Anterior:</span><b>{formatarReal(comparativo.economiaAnterior)}</b></div>
                            <div className={`${styles.compVar} ${corVariacaoClass(styles, comparativo.economiaVariacao)}`}>
                                {formatarPercent(comparativo.economiaVariacao)}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Totais do período (consistentes com as séries) */}
            <div className={styles.cardsPeriodo}>
                <div className={styles.cardSec}>
                    <div className={styles.cardTitulo}>TOTAL PRODUÇÃO (PERÍODO)</div>
                    <div className={styles.cardValor}>{formatarKwh(resumo?.producaoTotal)}</div>
                </div>
                <div className={styles.cardSec}>
                    <div className={styles.cardTitulo}>TOTAL CONSUMO (PERÍODO)</div>
                    <div className={styles.cardValor}>{formatarKwh(resumo?.consumoTotal)}</div>
                </div>
                <div className={styles.cardSec}>
                    <div className={styles.cardTitulo}>TOTAL ECONOMIA (PERÍODO)</div>
                    <div className={styles.cardValor}>{formatarReal(resumo?.economiaTotal)}</div>
                </div>
            </div>

            {/* Gráficos (SEM libs externas) */}
            <div className={styles.graficos}>
                <LineChartMini
                    title="Produção (kWh)"
                    data={serieProducao}
                    formatter={(v) => formatarNumero(v, 2)}
                />
                <LineChartMini
                    title="Consumo (kWh)"
                    data={serieConsumo}
                    formatter={(v) => formatarNumero(v, 2)}
                />
                <LineChartMini
                    title="Economia (R$)"
                    data={serieEconomia}
                    formatter={(v) => formatarNumero(v, 2)}
                />
            </div>

            {/* Operacional: feito x pendente por competência */}
            <div className={styles.statusBox}>
                <div className={styles.statusTitulo}>Acompanhamento Operacional (UCs geradas × pendentes)</div>

                {!statusPorCompetencia.length ? (
                    <div className={styles.semDadosOperacional}>Sem dados operacionais para o período.</div>
                ) : (
                    <table className={styles.tabela}>
                        <thead>
                        <tr>
                            <th>Competência</th>
                            <th>UCs</th>
                            <th>Geradas</th>
                            <th>Pendentes</th>
                        </tr>
                        </thead>
                        <tbody>
                        {statusPorCompetencia.map((s) => (
                            <tr key={s.competencia}>
                                <td>{s.competencia}</td>
                                <td>{s.totalUcs}</td>
                                <td>{s.geradas}</td>
                                <td className={Number(s.pendentes) > 0 ? styles.alerta : ''}>
                                    {s.pendentes}
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                )}
            </div>

            {loading ? (
                <div className={styles.loadingBar}>Carregando...</div>
            ) : null}
        </div>
    );
}
